import makeStyles from '@mui/styles/makeStyles';
import { FC, useEffect } from 'react';
import useFetchData from '../../../../hooks/useFetchData';
import useTitleBarSelect from '../../../../hooks/useTitleBarSelect';
import { RaptorTheme } from '../../../../styling/theme';
import {
  formatFundUrl,
  makeUniqueDataKey,
} from '../../../../utilities/urlBuilder';
import RiskComparisonTable, { ComparisonSection } from './RiskComparisonTable';
import dayjs from 'dayjs';
import useTitleBarDatePicker from '../../../../hooks/useTitleBarDatePicker';

import { useDispatch } from 'react-redux';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
  toggleTitleBarLoading,
} from '../../../../redux/pages/actions';
import {
  APPEND_NEW_DATE_PICKER_TO_TITLEBAR,
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_DATE_PICKER_FROM_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../../types/redux/pages/pageTypes';
import { DatePicker, Select } from '../../../../types/redux/pages/PagesStore';
import VarChangesTable from './VarChangesTable.component';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useRiskComparisonPageQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type { RiskComparisonPayload } from '../../../../api/rs-hg/risk/riskComparison.types';

const useRiskComparisonStyles = makeStyles((theme: RaptorTheme) => ({
  tabsRoot: {
    alignItems: 'center',
  },
  tabRoot: {
    zIndex: 2,
    borderRadius: 8,
    transition: 'all .3s',
    '&:hover': {
      color: theme.palette.primary.main,
      backgroundColor: theme.palette.grey[200],
    },
  },
  indicator: {
    zIndex: 1,
    height: '100%',
    position: 'absolute',
    transform: 'translateY(-50%)',
    top: '50%',
    display: 'flex',
    color: theme.palette.primary.main,
    justifyContent: 'center',
    backgroundColor: 'transparent',
    '& > div': {
      width: 'inherit',
      backgroundColor: theme.palette.grey[200],
      borderRadius: 8,
    },
  },
  flxContainer: {},
}));

const RiskComparison: FC<FundInfoComponentProps> = (props) => {
  const { fundId } = props;

  const classes = useRiskComparisonStyles();
  const dispatch = useDispatch();

  useEffect(() => {
    const selectsToAdd: Select[] = [
      {
        titleBarTitle: 'Changes: ',
        titleBarKey: 'changesSelection',
        values: [
          { label: 'Position', value: 'positionChanges' },
          { label: 'Exposure', value: 'exposureChanges' },
          { label: 'VaR', value: 'varBreakdown' },
        ],
        displayOnOverviewPage: false,
        aValueIsAlwaysSelected: true,
        currentValue: 'positionChanges',
      },
    ];
    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(
        removeTitleBarComponent(
          'changesSelection',
          REMOVE_SELECT_FROM_TITLEBAR,
        ),
      );
    };
  }, [dispatch]);

  const datesForSelects = useFetchData({
    url: formatFundUrl('get_fund_position_dates', fundId),
    keyName: makeUniqueDataKey(fundId, 'comparisonDates'),
    makeFetch: true,
  });

  const selectedSection = useTitleBarSelect(
    'changesSelection',
  ) as ComparisonSection;
  const fromDate = useTitleBarDatePicker('fromDate');
  const toDate = useTitleBarDatePicker('toDate');

  useEffect(() => {
    dispatch(toggleTitleBarLoading(true));
    if (datesForSelects && !datesForSelects.isFetching) {
      const datePickersToAdd: DatePicker[] = [
        {
          displayOnOverviewPage: true,
          titleBarKey: 'fromDate',
          titleBarTitle: 'From Date:',
          currentValue: dayjs(
            datesForSelects.data[0][1] ?? datesForSelects.data[0][0],
          ).format('YYYY-MM-DD'),
          availableDates: datesForSelects.data[0],
        },
        {
          displayOnOverviewPage: true,
          titleBarKey: 'toDate',
          titleBarTitle: 'To Date:',
          currentValue: dayjs(datesForSelects.data[0][0]).format('YYYY-MM-DD'),
          availableDates: datesForSelects.data[0],
        },
      ];
      dispatch(
        addAdditionalTitleBarComponents(
          datePickersToAdd,
          APPEND_NEW_DATE_PICKER_TO_TITLEBAR,
        ),
      );
      dispatch(toggleTitleBarLoading(false));
    }
    return () => {
      dispatch(
        removeTitleBarComponent('fromDate', REMOVE_DATE_PICKER_FROM_TITLEBAR),
      );
      dispatch(
        removeTitleBarComponent('toDate', REMOVE_DATE_PICKER_FROM_TITLEBAR),
      );
    };
  }, [datesForSelects, dispatch]);

  const datesReady = !!datesForSelects && !datesForSelects.isFetching;
  const datesSelected = !!fromDate && !!toDate;

  const riskComparisonQuery = useRiskComparisonPageQuery(
    fundId,
    fromDate,
    toDate,
    { enabled: datesReady && datesSelected },
  );

  return (
    <QueryBoundary<RiskComparisonPayload>
      isLoading={
        !datesReady || !datesSelected || riskComparisonQuery.isLoading
      }
      isFetching={riskComparisonQuery.isFetching}
      isError={riskComparisonQuery.isError}
      error={riskComparisonQuery.error}
      data={riskComparisonQuery.data?.data}
      loadingMessages={['Loading Comparison Data...']}
      noDataMessage="No comparison data available."
      errorMessage="Unable to load comparison data."
    >
      {(payload) => {
        const changesData =
          selectedSection === 'positionChanges'
            ? payload.position_differences_agg
            : selectedSection === 'exposureChanges'
              ? payload.exposure_changes
              : payload.position_var_differences;

        return selectedSection === 'varBreakdown' ? (
          <VarChangesTable
            key={`${fromDate}-${toDate}`}
            data={payload.position_var_differences}
            comparisonDate={fromDate}
            referenceDate={toDate}
          />
        ) : (
          <RiskComparisonTable
            key={`${selectedSection}-${fromDate}-${toDate}`}
            fundId={fundId}
            referenceDate={fromDate}
            comparisonDate={toDate}
            selectedSection={selectedSection}
            data={changesData}
            classes={classes}
          />
        );
      }}
    </QueryBoundary>
  );
};

export default RiskComparison;
